import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {useClientV2} from "../../../../utils/hooks";
import {
  LIST_STORE_SUBSCRIPTIONS_RQ_KEY,
  SEARCH_STORE_SUBSCRIPTIONS_RQ_KEY,
} from "../constants";
import {normalizeSubscriptionPaginatedResponse} from "../shared/normalize-subscription-page";
import type {
  ListSubscriptionsApiRawResponse,
  ListSubscriptionsApiResponse,
} from "../list-subscriptions/types";

export type UseSubscriptionsTableQueryParams = {
  enabled?: boolean;
  page: number;
  perPage: number;
  /** When non-empty, calls `GET subscriptions/search` instead of list. */
  searchQuery: string;
};

/**
 * Store table: server-paginated list via `GET subscriptions?page=&perPage=` or `subscriptions/search`.
 */
export const useSubscriptionsTableQuery = (params: UseSubscriptionsTableQueryParams) => {
  const client = useClientV2();
  const enabled = params.enabled ?? true;
  const q = params.searchQuery.trim();
  const isSearch = q.length > 0;

  const listQueryKey = isSearch
    ? [SEARCH_STORE_SUBSCRIPTIONS_RQ_KEY, {page: params.page, perPage: params.perPage, q}]
    : [LIST_STORE_SUBSCRIPTIONS_RQ_KEY, {page: params.page, perPage: params.perPage}];

  return useQuery<
    ListSubscriptionsApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: listQueryKey,
    queryFn: async () => {
      if (isSearch) {
        const sp = new URLSearchParams({
          page: String(params.page),
          perPage: String(params.perPage),
          q,
        });
        const raw = (await client(
          `subscriptions/search?${sp}`
        )) as ListSubscriptionsApiRawResponse;
        return normalizeSubscriptionPaginatedResponse(raw);
      }
      const lp = new URLSearchParams({
        page: String(params.page),
        perPage: String(params.perPage),
      });
      const raw = (await client(
        `subscriptions?${lp}`
      )) as ListSubscriptionsApiRawResponse;
      return normalizeSubscriptionPaginatedResponse(raw);
    },
    enabled,
    keepPreviousData: true,
    staleTime: Infinity,
    refetchOnWindowFocus: true,
  });
};
